import { DEFAULT_BASEMAP_ID, DEFAULT_VIEW_MODE } from './basemaps';
import type { MapViewMode } from './basemaps';
import type { EntityToggleState, FeatureOverlayState } from './mapLayers';
import { DEFAULT_ENTITY_TOGGLE_STATE, DEFAULT_FEATURE_OVERLAY_STATE } from './mapLayers';
import { MAP_PREFERENCES_KEY, mergeOverlayState, validateViewport } from './mapPreferences';
import type { MapViewport } from './mapPreferences';

// Split-model map preferences: the per-status poiTypes selection is superseded by the two
// entity toggles, so the storage shape changes and the key moves to v2.
//
// Rollback safety: the v1 key is deliberately LEFT INTACT — never deleted here — so a
// rolled-back portal build reads its own v1 state unchanged. The one-shot carry-over runs
// only while no v2 key exists (writing v2 is the re-migration guard); preference changes
// made under a rollback (written to v1) are not re-migrated once v2 exists — an accepted,
// documented trade. The v1 key is removed only by the post-release cleanup.

export const ENTITY_MAP_PREFERENCES_KEY = 'orbit.map.preferences.v2';

export interface EntityMapPreferences {
    selectedBasemapId: string;
    viewMode: MapViewMode;
    featureOverlays: FeatureOverlayState;
    entityToggles: EntityToggleState;
    viewport?: MapViewport;
}

function getDefaults(): EntityMapPreferences {
    return {
        selectedBasemapId: DEFAULT_BASEMAP_ID,
        viewMode: DEFAULT_VIEW_MODE,
        featureOverlays: { ...DEFAULT_FEATURE_OVERLAY_STATE },
        entityToggles: { ...DEFAULT_ENTITY_TOGGLE_STATE },
    };
}

function parseVersioned(raw: string, version: number): Record<string, unknown> | null {
    try {
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed !== 'object' || parsed === null) return null;
        if ((parsed as Record<string, unknown>)['version'] !== version) return null;
        return parsed as Record<string, unknown>;
    } catch {
        return null;
    }
}

function fromStored(
    stored: Record<string, unknown>,
    validBasemapIds: string[],
    entityToggles: unknown,
): EntityMapPreferences {
    const storedBasemapId = stored['selectedBasemapId'];
    const selectedBasemapId =
        typeof storedBasemapId === 'string' && validBasemapIds.includes(storedBasemapId)
            ? storedBasemapId
            : DEFAULT_BASEMAP_ID;

    const storedViewMode = stored['viewMode'];
    const viewMode: MapViewMode =
        storedViewMode === '2d' || storedViewMode === '3d' || storedViewMode === 'globe'
            ? storedViewMode
            : DEFAULT_VIEW_MODE;

    return {
        selectedBasemapId,
        viewMode,
        featureOverlays: mergeOverlayState(DEFAULT_FEATURE_OVERLAY_STATE, stored['featureOverlays']),
        entityToggles: mergeOverlayState(DEFAULT_ENTITY_TOGGLE_STATE, entityToggles),
        viewport: validateViewport(stored['viewport']),
    };
}

/**
 * Loads the split-model map preferences: v2 when present; otherwise a one-shot carry-over
 * from the intact v1 record (basemap, view mode, overlays, viewport — entity toggles
 * start at their defaults, since v1's per-status selection has no entity equivalent);
 * otherwise defaults.
 */
export function loadEntityMapPreferences(validBasemapIds: string[]): EntityMapPreferences {
    try {
        const rawV2 = localStorage.getItem(ENTITY_MAP_PREFERENCES_KEY);
        if (rawV2) {
            const stored = parseVersioned(rawV2, 2);
            if (stored) return fromStored(stored, validBasemapIds, stored['entityToggles']);
            return getDefaults();
        }

        const rawV1 = localStorage.getItem(MAP_PREFERENCES_KEY);
        if (rawV1) {
            const stored = parseVersioned(rawV1, 1);
            if (stored) return fromStored(stored, validBasemapIds, undefined);
        }

        return getDefaults();
    } catch {
        return getDefaults();
    }
}

/** Saves v2 preferences. Never touches the v1 key. Silently ignores write failures. */
export function saveEntityMapPreferences(prefs: EntityMapPreferences): void {
    try {
        localStorage.setItem(ENTITY_MAP_PREFERENCES_KEY, JSON.stringify({ version: 2, ...prefs }));
    } catch {
        // localStorage may be unavailable (private browsing, storage quota, etc.)
    }
}
